import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import type { AppUser } from "../../../services/userService";
import type { AggregatedNoteDto } from "../../../services/vorgangNoteService";
import type { MailLinkDto } from "../../../services/mailLinkService";
import { documentService, type DocumentDto } from "../../../services/documentService";
import type { NoteRefArt, NoteRefDto } from "../../../services/vorgangService";
import { NotizFeld } from "../../notizbloecke/NotizFeld";
import { bildMimeAusName } from "../../notizbloecke/bildDarstellung";
import { bezugKandidaten } from "../../notizbloecke/bezugKandidaten";
import type { Block } from "../../notizbloecke/blockModell";
import type { KommentarAendern } from "../../notizbloecke/KommentarFaden";
import type { KommentarZusatz } from "../../notizbloecke/kommentarInhalt";
import {
    kommentareJeAnker,
    kommentareJeZiel,
    randFaden,
    versteckteKommentare,
    zielFuerBlock,
    zielSchluessel,
    type Kommentar,
    type KommentarZiel,
} from "../../notizbloecke/kommentarModell";
import { useOpenDocument } from "../../documents/useDocumentViewer";
import { displayNameOf } from "../../telephony/telephonyUtils";
import { dokumenteAus, type VerlaufEintrag } from "../v3/verlauf";
import type { VerlaufTextEingabe } from "../v3/verlaufEingabe";
import { blockIstFormatierbar } from "./blockZuweisung";
import { archivZeilen, entwurfZeilen, mitEntwurfZeilen, type ArchivZeile } from "./archivZeilen";
import { EreignisListeV4, VERLAUF_DECKEL, vorhandeneArten } from "./EreignisListeV4";
import { ereignisZieleAus } from "./strangZaehlung";
import { AktionsLeisteV4 } from "./AktionsLeisteV4";
import { useEntwurfAktionen } from "./VorgangEntwuerfeV4";
import type { VorgangEntwurf } from "../../../services/graphMailService";
import "./vorgangV4.css";

/**
 * EIN Arbeitsstrang in V4 — der Vorgang selbst oder ein To-do.
 *
 * ══ DIE ORDNUNG (PO 15.09.2026) ═══════════════════════════════════════════
 *     Notizen · Verlauf · Trenner · Aktionsleiste
 *
 * Der Notizbereich ist wieder GETEILT. Die Zeichnung vom 10.09.2026 hatte den
 * Abschnitt „Verlauf" aufgelöst und Mail, Anruf, Dokument und Termin als
 * Karten zwischen die Notizen gestellt. Am 15.09.2026 hat der PO das
 * zurückgenommen: „wir wollen den Notizbereich doch aufteilen in Notizen und
 * Verlauf … der Verlauf dann so wie das Archiv, bei mehreren To-dos im To-do
 * und bei einem Vorgang ohne To-do dann Archiv" — untereinander, nicht
 * nebeneinander (Artifact „V4 Notizen und Verlauf").
 *
 *  · NOTIZEN: nur, was hier geschrieben wurde — seit dem 20.09.2026 EIN
 *    Textfeld (`NotizFeld`) statt vieler Blöcke; neueste zuerst, die
 *    Schreibstelle oben (PO 14.09.2026).
 *  · VERLAUF (`mitVerlauf`): die Ereignisse DIESES Strangs als Karten
 *    (`EreignisListeV4`), mit Vorschau, Rechtsklick und — seit dem 18.09.2026
 *    — der Kommentarspalte rechts daneben. Ein Vorgang OHNE To-do setzt
 *    `mitVerlauf={false}`: sein Verlauf ist das Archiv unter der Karte.
 * ═══════════════════════════════════════════════════════════════════════════
 */

export type StrangHandlers = {
    onOeffneMail: (link: MailLinkDto) => void;
    /** Neue Notiz aus der Eingabezeile. */
    /**
     * Neuer Text aus der Schreibstelle. Der zurückgegebene Schlüssel lässt das
     * Feld den Abschnitt AN ORT UND STELLE übernehmen — ohne dass der Text
     * kurz verschwindet und der Cursor springt.
     */
    onNotizNeu: (
        input: VerlaufTextEingabe,
        todoId: number | undefined,
        format?: "text" | "html",
        refs?: NoteRefDto[],
    ) => Promise<{ key: string } | void>;
    onBlockAendern: (block: Block, html: string, refs?: NoteRefDto[], mentions?: string[]) => Promise<void>;
    /** Ein Abschnitt wurde LEERGERÄUMT — die Notiz gehört gelöscht (PO 13.09.2026). */
    onBlockLoeschen: (block: Block) => Promise<void>;
    /**
     * Rückfrage vor dem Löschen mehrerer oder fremder Abschnitte. Eine
     * gelöschte Notiz lässt sich in der Oberfläche nicht wiederherstellen —
     * der Papierkorb kennt nur ganze Vorgänge.
     */
    bestaetigeLoeschen?: (anzahl: number, fremde: number) => boolean | Promise<boolean>;
    /** Rechtsklick auf eine Ereigniskarte — dort hängt die To-do-Zuweisung. */
    onEreignisMenue?: (eintrag: VerlaufEintrag, e: React.MouseEvent) => void;
    /**
     * Einen Kommentar an einem Block oder einer Ereigniskarte anlegen
     * (PO 13./14.09.2026). Fehlt er, sind die Fäden nur zu lesen — der Strang
     * weiß nicht, wie geschrieben wird, das weiß die Seite.
     */
    onKommentarNeu?: (
        ziel: KommentarZiel,
        text: string,
        mentions: string[],
        anker?: string,
        /** `#`-Bezüge und Anhänge (PO 28.09.2026) — hochgeladen wird auf der Seite. */
        zusatz?: KommentarZusatz,
    ) => Promise<void>;
    /**
     * Einen EIGENEN Kommentar korrigieren bzw. zurücknehmen. Der Beitrag
     * nennt seine Tabelle selbst (`quelle`) — der Strang reicht ihn nur
     * durch, geschrieben wird auf der Seite.
     */
    onKommentarAendern?: KommentarAendern;
    onKommentarLoeschen?: (k: Kommentar) => Promise<void>;
    /**
     * Den Haken am Kommentar setzen (PO 18.09.2026). Bewusst NICHT an `canEdit`
     * gebunden: abhaken darf, wer den Vorgang sieht (PO 20.09.2026).
     */
    onKommentarErledigt?: (k: Kommentar, erledigt: boolean) => Promise<void>;
    /**
     * Ein Bild für eine Notiz hochladen (PO 14.09.2026: „bilder in die notiz
     * laden"). Die Seite weiß, wohin (an den Vorgang, Kategorie „Notizbild");
     * der Strang reicht nur durch. Fehlt er, gibt es keinen Bild-Upload.
     */
    onBildHochladen?: (file: File) => Promise<{ id: number }>;
    onGeaendert: () => void | Promise<void>;
};

type Props = StrangHandlers & {
    /** Die Einträge DIESES Strangs — sie werden zu Blöcken. */
    eintraege: VerlaufEintrag[];
    /** ALLE Notizen der Seite; die Fläche filtert selbst auf ihren Strang. */
    alleNotizen: AggregatedNoteDto[];
    users: AppUser[];
    canEdit: boolean;
    aktuellerUserId: string;
    vorgangId: number;
    todoId?: number;
    contactId?: number | null;
    contactName?: string | null;
    vorgangNbr?: string | null;
    vertragNr?: string | null;
    risikoName?: string | null;
    zugewieseneMessageIds: Set<string>;
    /** Schon im Strom liegende Dokumente — die Auswahl graut sie aus. */
    vorhandeneDokumentIds?: ReadonlySet<number>;
    risiken?: Array<{ id: number; label: string }>;
    vertraege?: Array<{ id: number; label: string }>;
    /**
     * Den Verlauf unter den Notizen zeichnen. Nur der Vorgang OHNE To-do
     * setzt ihn auf `false`: dort ist das Archiv am Seitenende der Verlauf.
     */
    mitVerlauf?: boolean;
    /**
     * Mail-Entwürfe DIESES Strangs (User 22.10.2026) — sie stehen als Karte im
     * Verlauf wie eine Mail, gekennzeichnet als „Entwurf", mit Kommentaren.
     * `null` = noch nicht geladen; fehlt der Wert, kennt der Aufrufer keine.
     */
    entwuerfe?: ReadonlyArray<VorgangEntwurf> | null;
    /**
     * NUR DAS NOTIZFELD — ohne Verlauf und ohne Aktionsleiste (User
     * 01.11.2026: „Ein Vorgang mit To-dos hat auch einen Notiz-Bereich, nur
     * ein Notiz-Bereich, nicht Verlauf"). Mails, Dokumente und Anrufe bleiben
     * an den To-dos; die laufende Notiz des Vorgangs zeichnet nur den Text.
     */
    nurNotizen?: boolean;
    /** Überschrift des Notizbereichs; Vorgabe „Notizen". */
    notizTitel?: string;
    /**
     * Ohne Trenner und Aktionsleiste — auf dem Handy übernimmt das Plus
     * (Plan „Vorgangsdetail mobil", PR 3). Notizen und Verlauf bleiben.
     */
    ohneAktionsleiste?: boolean;
    /** Handy: kompakte Verlaufskarten (Plan mobil, PR 6). */
    kompakt?: boolean;
};

export const ArbeitsstrangV4: React.FC<Props> = ({
    eintraege,
    alleNotizen,
    users,
    canEdit,
    aktuellerUserId,
    vorgangId,
    todoId,
    contactId,
    contactName,
    vorgangNbr,
    vertragNr,
    risikoName,
    zugewieseneMessageIds,
    vorhandeneDokumentIds,
    risiken,
    vertraege,
    mitVerlauf: mitVerlaufWunsch = true,
    entwuerfe,
    nurNotizen = false,
    notizTitel = "Notizen",
    ohneAktionsleiste = false,
    kompakt = false,
    onOeffneMail,
    onNotizNeu,
    onBlockAendern,
    onBlockLoeschen,
    bestaetigeLoeschen,
    onEreignisMenue,
    onKommentarNeu,
    onKommentarAendern,
    onKommentarLoeschen,
    onKommentarErledigt,
    onBildHochladen,
    onGeaendert,
}) => {
    const navigate = useNavigate();
    const openDocument = useOpenDocument();
    const mitVerlauf = mitVerlaufWunsch && !nurNotizen;

    const dokumentOeffnen = useCallback(
        (d: DocumentDto) => {
            openDocument(d, { download: (docId) => void documentService.openSignedDownload(docId) });
        },
        [openDocument],
    );

    /** Ein Notizbild groß ansehen — es steht in keiner Dokumentliste, nur im Text. */
    const bildOeffnen = useCallback(
        ({ id, name }: { id: number; name: string }) =>
            openDocument(
                { id, fileName: name, mimeType: bildMimeAusName(name) },
                { download: (docId) => void documentService.openSignedDownload(docId) },
            ),
        [openDocument],
    );

    /**
     * Das Angebot hinter dem `#`: was an DIESEM Strang hängt. Unverändert aus
     * V3 — worauf man sich bezieht, hat man in der Regel gerade vor Augen.
     */
    const kandidaten = useMemo(
        () =>
            bezugKandidaten({
                mails: eintraege
                    .filter((e) => e.mail)
                    .map((e) => ({
                        id: e.mail!.id,
                        subject: e.mail!.subject,
                        from: e.mail!.senderName,
                        receivedAt: e.mail!.receivedAt,
                        preview: e.mail!.preview,
                    })),
                anrufe: eintraege
                    .filter((e) => e.anruf)
                    .map((e) => ({
                        id: e.anruf!.id,
                        displayName: displayNameOf(e.anruf!),
                        startedAt: e.anruf!.startedAt,
                    })),
                dokumente: dokumenteAus(eintraege).map((d) => ({ id: d.id, fileName: d.fileName })),
                risiken: risiken ?? [],
                vertraege: vertraege ?? [],
                // KEINE PERSONEN mehr hinter `#` (PO 28.09.2026: „Personen bei #
                // raus"). Personen erwähnt man mit `@` — zwei Wege zur selben
                // Person verwirrten nur, und der `#`-Chip einer Person
                // benachrichtigte niemanden.
            }),
        [eintraege, risiken, vertraege],
    );

    /**
     * Klick auf einen `#`-Bezug im Notiztext. Mails und Dokumente stehen als
     * vollständige Objekte in `eintraege`, Risiko und Vertrag brauchen nur
     * ihre Id. Anruf und Person haben kein Ziel — sie bleiben Auszeichnung.
     */
    const bezugOeffnen = useCallback(
        (art: NoteRefArt, id: string) => {
            if (art === "mail") {
                const treffer = eintraege.find((e) => e.mail && String(e.mail.id) === id);
                if (treffer?.mail) onOeffneMail(treffer.mail);
                return;
            }
            if (art === "dokument") {
                const d = dokumenteAus(eintraege).find((x) => String(x.id) === id);
                if (d) dokumentOeffnen(d);
                // Ein Anhang aus einem Kommentar kann ein Notizbild sein, das
                // in keiner Dokumentliste steht — dann direkt herunterladen.
                else if (Number(id) > 0) void documentService.openSignedDownload(Number(id));
                return;
            }
            if (art === "risiko") navigate(`/risks/${id}`);
            else if (art === "vertrag") navigate(`/contracts/${id}`);
        },
        [eintraege, navigate, onOeffneMail, dokumentOeffnen],
    );

    /**
     * Die Ziele der Ereignisse DIESES Strangs — `mail:7`, `anruf:8`,
     * `dokument:9`. Nur ihre Kommentare dürfen hinter die Sprechblase der
     * Verlaufszeile; ein Kommentar an einer Mail, die anderswo hängt, bleibt
     * sichtbarer Block (`versteckteKommentare`). Beim Vorgang ohne To-do
     * steht die Sprechblase an der Archivzeile — das Ziel gilt dort genauso.
     */
    const ereignisZiele = useMemo(() => {
        const ziele = ereignisZieleAus(eintraege);
        // Entwürfe (User 22.10.2026): ihr Faden steht an ihrer Verlaufskarte.
        // Solange die Liste lädt (`null`), bleiben ALLE Entwurfs-Kommentare
        // dahinter — sonst blitzten sie kurz als Block auf. Ist der Entwurf
        // danach fort (gelöscht, in Outlook gesendet), steht sein Kommentar
        // als gewöhnlicher Block da: nichts geht verloren.
        if (entwuerfe === null) {
            for (const n of alleNotizen) if (n.entwurfId) ziele.add(`entwurf:${n.entwurfId}`);
        } else {
            for (const e of entwuerfe ?? []) ziele.add(`entwurf:${e.draftId}`);
        }
        return ziele;
    }, [eintraege, entwuerfe, alleNotizen]);

    /**
     * DIE KOMMENTARE WANDERN AUS DEM STROM (PO 13./14.09.2026).
     *
     * Bis hierher stand jeder Kommentar als gleichrangiger Block zwischen den
     * Notizen — ein Kommentar an einer Mail sogar ohne jeden sichtbaren Bezug,
     * als nackter Absatz. Jetzt hängt er hinter der Sprechblase des Elements,
     * auf das er zeigt. Was sich NICHT wieder öffnen ließe (verwaist, leeres
     * Elternteil, Antwort auf einen Kommentar), bleibt Block — die Regel steht
     * in `versteckteKommentare`.
     */
    const kommentarKeys = useMemo(
        () => versteckteKommentare(alleNotizen, ereignisZiele),
        [alleNotizen, ereignisZiele],
    );

    /** Alle Fäden auf einen Griff — die Fläche fragt einmal je Block. */
    const faeden = useMemo(() => kommentareJeZiel(alleNotizen), [alleNotizen]);
    /**
     * Die Fäden an MARKIERTEN TEXTSTELLEN (PO 18.09.2026), geschlüsselt nach
     * ihrem Anker. Sie gehören zu keinem Ziel im Verlauf — sie hängen mitten
     * im Text einer Notiz.
     */
    const ankerFaeden = useMemo(() => kommentareJeAnker(alleNotizen), [alleNotizen]);
    const fadenZu = useCallback(
        (ziel: KommentarZiel) => faeden.get(zielSchluessel(ziel)) ?? [],
        [faeden],
    );

    /**
     * Die Zeilen des Verlaufs — dieselbe Ableitung wie im Archiv, nur ohne
     * Notizen und ohne Herkunft: alles hier gehört zu diesem Strang.
     */
    const verlaufZeilen = useMemo(
        () =>
            mitVerlauf
                ? mitEntwurfZeilen(
                      archivZeilen({ eintraege, notizen: [] }, contactName ?? null),
                      entwurfZeilen(entwuerfe ?? [], todoId ?? null),
                  )
                : [],
        [mitVerlauf, eintraege, contactName, entwuerfe, todoId],
    );
    const entwurfAktionen = useEntwurfAktionen(onGeaendert);
    const nachKey = useMemo(() => new Map(eintraege.map((e) => [e.key, e])), [eintraege]);

    /*
     * „Verlauf durchsuchen" steht seit dem 18.09.2026 in der FUSSLEISTE
     * (PO-Zettel) und klappt Suchfeld und Filterchips über der Liste auf; ein
     * zweiter Klick räumt sie weg. Über dem Verlauf stand sonst dauerhaft eine
     * Zeile, die man selten braucht.
     */
    const [suchOffen, setSuchOffen] = useState(false);

    return (
        <>
            <div className="vg4-notiz">
                {/* KEIN ZÄHLER (PO 17.09.2026: „nimm die Anzahl raus"). Eine
                    Fläche ist eins, egal wie viel darin steht — die Zahl
                    zählte Blöcke, die man als Absätze liest. */}
                <div className="vg4-teil-kopf">
                    <span className="vg4-teil-t">{notizTitel}</span>
                </div>
                <NotizFeld
                    notizen={alleNotizen}
                    todoId={todoId ?? null}
                    users={users}
                    canEdit={canEdit}
                    aktuellerUserId={aktuellerUserId}
                    onNeu={(html, refs, mentions) =>
                        onNotizNeu(
                            { text: html, mentions: mentions ?? [], zuInformieren: [] },
                            todoId,
                            "html",
                            refs,
                        )
                    }
                    onAendern={onBlockAendern}
                    onLoeschen={onBlockLoeschen}
                    {...(bestaetigeLoeschen ? { bestaetigeLoeschen } : {})}
                    /* Seit dem 20.09.2026 hat auch die Bestands-`TodoNote` ein
                       `format` — formatiert werden darf überall
                       (`blockIstFormatierbar`). */
                    formatierbarFuer={blockIstFormatierbar}
                    onBezugOeffnen={bezugOeffnen}
                    kandidaten={kandidaten}
                    kommentarKeys={kommentarKeys}
                    kommentareFuer={(block) =>
                        randFaden(fadenZu(zielFuerBlock(block, todoId ?? null)), block.html)
                    }
                    ankerFaeden={ankerFaeden}
                    {...(onKommentarNeu
                        ? {
                              onKommentarNeu: (
                                  block: Block,
                                  text: string,
                                  mentions: string[],
                                  anker?: string,
                                  zusatz?: KommentarZusatz,
                              ) =>
                                  onKommentarNeu(
                                      zielFuerBlock(block, todoId ?? null),
                                      text,
                                      mentions,
                                      anker,
                                      zusatz,
                                  ),
                              kommentarMitAnhang: true,
                          }
                        : {})}
                    {...(onKommentarErledigt ? { onKommentarErledigt } : {})}
                    {...(onKommentarAendern ? { onKommentarAendern } : {})}
                    {...(onKommentarLoeschen ? { onKommentarLoeschen } : {})}
                    {...(onBildHochladen ? { onBildHochladen } : {})}
                    onBildOeffnen={bildOeffnen}
                />
            </div>

            {/* KEINE KOPFZEILE MIT ZÄHLER (PO 18.09.2026: „die Kopfzeile bei
                Verlauf rausnehmen"). „Verlauf · 7 Einträge" war Beiwerk; die
                Zahl sagt nichts, was die Karten nicht selbst sagen.

                Das WORT „Verlauf" steht seit dem 20.09.2026 wieder da — als
                Spaltentitel über der linken Hälfte (`spaltenTitel`), nicht als
                Abschnittskopf: seit die Kommentare rechts danebenstehen, muss
                erkennbar sein, welche Spalte welche ist. */}
            {mitVerlauf && (
                <section className="vg4-verlauf" aria-label="Verlauf">
                    <EreignisListeV4
                        zeilen={verlaufZeilen}
                        arten={vorhandeneArten(verlaufZeilen)}
                        mitHerkunft={false}
                        leerHinweis="Noch keine Mails, Anrufe, Dokumente oder Termine."
                        suchPlatzhalter="In diesem Verlauf suchen …"
                        deckel={VERLAUF_DECKEL}
                        spaltenTitel="Verlauf"
                        kompakt={kompakt}
                        suchOffen={suchOffen}
                        users={users}
                        onOeffneMail={onOeffneMail}
                        onOeffneEntwurf={entwurfAktionen.oeffnen}
                        onEntwurfLoeschen={(e) => void entwurfAktionen.loeschen(e)}
                        onOeffneTermin={(terminId) => navigate(`/termine/notizen/${terminId}`)}
                        {...(onEreignisMenue
                            ? {
                                  onKontextmenue: (z: ArchivZeile, ev: React.MouseEvent) => {
                                      const eintrag = nachKey.get(z.key);
                                      if (eintrag) onEreignisMenue(eintrag, ev);
                                  },
                              }
                            : {})}
                        kommentare={{
                            fadenZu,
                            users,
                            ...(canEdit ? { aktuellerUserId } : {}),
                            ...(onKommentarNeu && canEdit ? { onNeu: onKommentarNeu } : {}),
                            ...(onKommentarAendern && canEdit ? { onAendern: onKommentarAendern } : {}),
                            ...(onKommentarLoeschen && canEdit ? { onLoeschen: onKommentarLoeschen } : {}),
                            // OHNE `canEdit`: „erstmal jeder" darf abhaken
                            // (PO 20.09.2026) — wer nur lesen darf, kann die
                            // an ihn gerichtete Bitte trotzdem erledigen.
                            ...(onKommentarErledigt ? { onErledigt: onKommentarErledigt } : {}),
                            kandidaten,
                            onBezugOeffnen: bezugOeffnen,
                            mitAnhang: true,
                        }}
                    />
                </section>
            )}

            {nurNotizen || ohneAktionsleiste ? null : (
                <>
                    <div className="vg4-trenner" role="presentation" />

                    <AktionsLeisteV4
                        {...(mitVerlauf
                            ? {
                                  suche: {
                                      offen: suchOffen,
                                      onUmschalten: () => setSuchOffen((o) => !o),
                                  },
                              }
                            : {})}
                        vorgangId={vorgangId}
                        todoId={todoId}
                        contactId={contactId}
                        contactName={contactName}
                        vorgangNbr={vorgangNbr}
                        vertragNr={vertragNr}
                        risikoName={risikoName}
                        zugewieseneMessageIds={zugewieseneMessageIds}
                        vorhandeneDokumentIds={vorhandeneDokumentIds}
                        canEdit={canEdit}
                        onGeaendert={onGeaendert}
                    />
                </>
            )}
        </>
    );
};
